import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './UnsavedChanges.css';
const dirtyForms = new Set();
let showWarning = null;
let allowUnload = false;
let navigationConfirmed = false;
export function runConfirmedAdminNavigation(action) {
  navigationConfirmed = true;
  try { action(); } finally { navigationConfirmed = false; }
}
export const hasAdminUnsavedChanges = () => dirtyForms.size > 0;
export function guardAdminNavigation(action) {
  if (navigationConfirmed || !hasAdminUnsavedChanges()) { action(); return; }
  showWarning?.(() => { allowUnload = true; try { action(); } finally { window.setTimeout(() => { allowUnload = false; }, 0); } });
}
export function confirmAdminAction(description, title = 'Confirmar acción') {
  return new Promise(resolve => {
    if (!showWarning) { resolve(false); return; }
    showWarning({ action: () => resolve(true), cancel: () => resolve(false), title, description });
  });
}
export function useAdminDirty(dirty) {
  const key = useRef(Symbol('admin-form'));
  useLayoutEffect(() => {
    const id = key.current;
    if (dirty) dirtyForms.add(id); else dirtyForms.delete(id);
    return () => dirtyForms.delete(id);
  }, [dirty]);
}
export function useAdminDraft(value, identity, enabled = true) {
  const serialized = JSON.stringify(value);
  const baseline = useRef({ identity, value: serialized });
  const [, rerender] = useState(0);
  if (baseline.current.identity !== identity || !enabled) baseline.current = { identity, value: serialized };
  useAdminDirty(enabled && baseline.current.value !== serialized);
  return (nextValue = value) => { baseline.current = { identity, value: JSON.stringify(nextValue) }; rerender(v => v + 1); };
}
export default function UnsavedChangesHost() {
  const [pending, setPending] = useState(null);
  const dialog = useRef(null);
  useEffect(() => {
    showWarning = value => setPending(() => typeof value === 'function' ? { action: value } : value);
    const beforeUnload = event => {
      if (allowUnload || !hasAdminUnsavedChanges()) return;
      event.preventDefault(); event.returnValue = '';
    };
    const followLink = event => {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || !hasAdminUnsavedChanges()) return;
      const link = event.target.closest?.('a[href]');
      if (!link || link.hasAttribute('download') || link.target === '_blank' || link.getAttribute('href').startsWith('#')) return;
      event.preventDefault();
      guardAdminNavigation(() => window.location.assign(link.href));
    };
    document.addEventListener('click', followLink);
    window.addEventListener('beforeunload', beforeUnload);
    return () => { showWarning = null; document.removeEventListener('click', followLink); window.removeEventListener('beforeunload', beforeUnload); };
  }, []);
  useEffect(() => {
    if (!pending) return;
    const previous = document.activeElement;
    dialog.current?.querySelector('button')?.focus();
    const keydown = e => {
      if (e.key === 'Escape') { e.preventDefault(); pending.cancel?.(); setPending(null); }
      if (e.key === 'Tab') {
        const buttons = dialog.current?.querySelectorAll('button');
        if (!buttons?.length) return;
        if (e.shiftKey && document.activeElement === buttons[0]) { e.preventDefault(); buttons[buttons.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === buttons[buttons.length - 1]) { e.preventDefault(); buttons[0].focus(); }
      }
    };
    document.addEventListener('keydown', keydown, true);
    return () => { document.removeEventListener('keydown', keydown, true); previous?.focus?.(); };
  }, [pending]);
  return pending ? createPortal(<div className="wisby-leave-backdrop"><section className="wisby-leave-dialog" ref={dialog} role="alertdialog" aria-modal="true" aria-labelledby="wisby-leave-title" aria-describedby="wisby-leave-description"><span className="wisby-leave-icon" aria-hidden="true">!</span><span className="wisby-leave-kicker">{pending.title ? 'Administración' : 'Cambios pendientes'}</span><h2 id="wisby-leave-title">{pending.title || <>Antes de <span>salir</span></>}</h2><p id="wisby-leave-description">{pending.description || 'Tienes información sin guardar. Si sales ahora, perderás los cambios pendientes.'}</p><div className="wisby-leave-actions"><button onClick={() => { const action = pending.action; setPending(null); action(); }}>{pending.title ? 'Confirmar' : 'Salir sin guardar'}</button><button className="primary" onClick={() => { pending.cancel?.(); setPending(null); }}>{pending.title ? 'Cancelar' : 'Seguir editando'}</button></div></section></div>, document.body) : null;
}
